import type { GeneratedWorkout, UserProfile, WorkoutCompletion } from "@/types";

export const PROFILE_SCHEMA_VERSION = 2;
export const WORKOUT_SCHEMA_VERSION = 2;
export const COMPLETION_SCHEMA_VERSION = 2;
export const PLANNER_VERSION = "2026.2";
export const WORKOUT_LIBRARY_VERSION = "2026.1";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function migrateProfile(value: unknown): UserProfile | null {
  if (!isRecord(value) || typeof value.accountId !== "string") return null;
  const profile = { ...value } as UserProfile;
  const days = Array.isArray(profile.availabilityDays) ? profile.availabilityDays : [];
  profile.availabilityDays = [...new Set(days.filter((day): day is number => Number.isInteger(day) && day >= 0 && day <= 6))];
  if (!["beginner", "intermediate", "advanced"].includes(profile.experienceLevel)) profile.experienceLevel = "beginner";
  if (!["base", "build", "improve_ftp", "race_prep"].includes(profile.goal)) profile.goal = "build";
  if (!["indoor", "outdoor", "mix"].includes(profile.indoorOutdoorPreference)) profile.indoorOutdoorPreference = "mix";
  profile.powerMeterSupported = profile.powerMeterSupported === true;
  profile.hrSupported = profile.hrSupported === true;
  profile.defaultDurationMinutes = Number.isFinite(profile.defaultDurationMinutes)
    ? Math.min(480, Math.max(20, Math.round(profile.defaultDurationMinutes)))
    : 60;
  if (profile.ftpWatts != null && (!Number.isFinite(profile.ftpWatts) || profile.ftpWatts < 50 || profile.ftpWatts > 700)) delete profile.ftpWatts;
  if (profile.weightKg != null && (!Number.isFinite(profile.weightKg) || profile.weightKg < 25 || profile.weightKg > 250)) delete profile.weightKg;
  profile.schemaVersion = PROFILE_SCHEMA_VERSION;
  profile.revision = Number.isFinite(profile.revision) ? profile.revision : 0;
  return profile;
}

export function migrateWorkout(value: unknown): GeneratedWorkout | null {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.accountId !== "string" ||
      typeof value.workoutName !== "string" || typeof value.weekStartDate !== "string" || !Array.isArray(value.blocksResolved)) return null;
  const workout = { ...(value as GeneratedWorkout), schemaVersion: WORKOUT_SCHEMA_VERSION };
  if (!["power", "hr", "rpe"].includes(workout.targetsMode)) workout.targetsMode = "rpe";
  return workout;
}

export function migrateWorkouts(value: unknown): GeneratedWorkout[] {
  if (!Array.isArray(value)) return [];
  return value.map(migrateWorkout).filter((v): v is GeneratedWorkout => v !== null);
}

export function migrateCompletion(value: unknown): WorkoutCompletion | null {
  if (!isRecord(value) || typeof value.workoutId !== "string" || typeof value.completedAt !== "string") return null;
  const completion = { ...(value as WorkoutCompletion), schemaVersion: COMPLETION_SCHEMA_VERSION };
  if (!["completed", "partial", "skipped", "planned"].includes(completion.status)) return null;
  if (completion.rpe != null) completion.rpe = Math.min(10, Math.max(1, Math.round(completion.rpe)));
  return completion;
}

/** Stable, non-cryptographic fingerprint used to explain when a plan changed. */
export function fingerprintPlanInput(value: unknown): string {
  const text = JSON.stringify(value, Object.keys((value ?? {}) as object).sort());
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}
